import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface ConfirmDialog {
  visible: boolean;
  title: string;
  message: string;
  onConfirm: (() => void) | null;
}

// Activity event callback types
export type ActivityEventCallback = () => void

// Inactivity timeout in milliseconds (15 minutes)
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000
// Inactivity check interval (check every 30s)
const INACTIVITY_CHECK_INTERVAL_MS = 30 * 1000

export const useUIStore = defineStore('ui', () => {
  // State
  const globalLoading = ref(false)
  const navigating = ref(false)
  const connectionWarning = ref(false)
  const activeTab = ref<'estimation' | 'confidence'>('estimation')
  const manageVotersMode = ref(false)
  const sharePopupVisible = ref(false)
  const confirmDialog = ref<ConfirmDialog>({
    visible: false,
    title: '',
    message: '',
    onConfirm: null
  })
  
  // Inactivity state (Requirements: 15.1, 15.5)
  const inactivityPaused = ref(false)
  const lastActivityTimestamp = ref(Date.now())
  
  // Internal timer for inactivity detection
  let inactivityCheckTimer: ReturnType<typeof setInterval> | null = null
  
  // Event callbacks for activity state changes
  const inactivityCallbacks: Set<ActivityEventCallback> = new Set()
  const resumeActivityCallbacks: Set<ActivityEventCallback> = new Set()

  // Actions
  function setGlobalLoading(loading: boolean) {
    globalLoading.value = loading
  }

  function setNavigating(isNavigating: boolean) {
    navigating.value = isNavigating
  }

  function setConnectionWarning(warning: boolean) {
    connectionWarning.value = warning
  }

  function setActiveTab(tab: 'estimation' | 'confidence') {
    activeTab.value = tab
  }

  function toggleManageVotersMode() {
    manageVotersMode.value = !manageVotersMode.value
  }

  function setManageVotersMode(mode: boolean) {
    manageVotersMode.value = mode
  }

  function setSharePopupVisible(visible: boolean) {
    sharePopupVisible.value = visible
  }

  function showConfirmDialog(title: string, message: string, onConfirm: () => void) {
    confirmDialog.value = {
      visible: true,
      title,
      message,
      onConfirm
    }
  }

  function hideConfirmDialog() {
    confirmDialog.value = {
      visible: false,
      title: '',
      message: '',
      onConfirm: null
    }
  }

  // Inactivity management actions (Requirements: 15.1, 15.5)
  
  /**
   * Registers a callback to be invoked when inactivity is detected.
   * Returns an unsubscribe function.
   */
  function onInactivity(callback: ActivityEventCallback): () => void {
    inactivityCallbacks.add(callback)
    return () => inactivityCallbacks.delete(callback)
  }

  /**
   * Registers a callback to be invoked when activity resumes after inactivity.
   * Returns an unsubscribe function.
   */
  function onResumeActivity(callback: ActivityEventCallback): () => void {
    resumeActivityCallbacks.add(callback)
    return () => resumeActivityCallbacks.delete(callback)
  }

  /**
   * Emits the inactivity event to all registered callbacks.
   */
  function emitInactivity() {
    inactivityCallbacks.forEach(callback => callback())
  }

  /**
   * Emits the resume activity event to all registered callbacks.
   */
  function emitResumeActivity() {
    resumeActivityCallbacks.forEach(callback => callback())
  }

  /**
   * Records user activity by updating the timestamp.
   * If currently paused due to inactivity, automatically resumes and emits resumeActivity event.
   * Should be called on user actions like voting, revealing, clearing, tab changes.
   */
  function recordActivity() {
    lastActivityTimestamp.value = Date.now()
    if (inactivityPaused.value) {
      inactivityPaused.value = false
      console.log('Activity detected while paused, resuming')
      emitResumeActivity()
    }
  }

  /**
   * Checks if the user has been inactive for more than 15 minutes.
   * Returns true if inactivity timeout has been reached.
   */
  function checkInactivity(): boolean {
    const now = Date.now()
    const elapsed = now - lastActivityTimestamp.value
    return elapsed >= INACTIVITY_TIMEOUT_MS
  }

  /**
   * Internal function called when inactivity is detected.
   * Sets the paused flag and emits the inactivity event.
   */
  function handleInactivityDetected() {
    if (!inactivityPaused.value) {
      console.log('Inactivity timeout reached, pausing')
      inactivityPaused.value = true
      emitInactivity()
    }
  }

  /**
   * Starts the activity monitoring timer.
   * Should be called when entering a view that needs inactivity detection.
   * Requirements: 15.1, 15.5
   */
  function startActivityMonitoring() {
    // Clear any existing timer
    stopActivityMonitoring()
    
    // Initialize the activity timestamp
    lastActivityTimestamp.value = Date.now()
    inactivityPaused.value = false
    
    inactivityCheckTimer = setInterval(() => {
      if (checkInactivity() && !inactivityPaused.value) {
        handleInactivityDetected()
      }
    }, INACTIVITY_CHECK_INTERVAL_MS)
  }

  /**
   * Stops the activity monitoring timer.
   * Should be called when leaving a view that uses inactivity detection.
   */
  function stopActivityMonitoring() {
    if (inactivityCheckTimer !== null) {
      clearInterval(inactivityCheckTimer)
      inactivityCheckTimer = null
    }
  }

  return {
    // State
    globalLoading,
    navigating,
    connectionWarning,
    activeTab,
    manageVotersMode,
    sharePopupVisible,
    confirmDialog,
    inactivityPaused,
    lastActivityTimestamp,
    // Actions
    setGlobalLoading,
    setNavigating,
    setConnectionWarning,
    setActiveTab,
    toggleManageVotersMode,
    setManageVotersMode,
    setSharePopupVisible,
    showConfirmDialog,
    hideConfirmDialog,
    // Activity monitoring
    recordActivity,
    startActivityMonitoring,
    stopActivityMonitoring,
    onInactivity,
    onResumeActivity,
  }
})